import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Save } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import BaseDataTable, {
  IColumnConfig,
  IDataRow,
} from '../../../../components/ui/baseDataTable';
import ButtonV2 from '../../../../components/ui/button/v2';
import { ErrorDisplay } from '../../../../components/ui/error/ErrorDisplay';
import { CustomLoadingSpinner } from '../../../../components/ui/spinners/LoadingSpinner';
import NoDataIcon from '../../../../assets/svg/EmptyTableIcon';
import { RETRIEVING_DATA_SUBTITLE } from '../../../../lib/constants';
import { cn } from '../../../../lib/utils';
import { showErrorToast, showSuccessToast } from '../../../../lib/toast';
import {
  ActionAgainstPerpetrator,
  ActionAgainstPerpetratorUI,
} from '../../../../api/actions_against_perpetrators';
import { getCollaborators } from '../../../users/logic/slice';
import {
  selectInvolvedPartyOptions,
  selectPerpetratorActionsError,
  selectPerpetratorActionsStatus,
  selectRecordedPerpetratorActions,
} from '../../logic/ActionsAgainstPerpetratorsSlice';
import {
  fetchActionsAgainstPerpetratorsThunk,
  upsertActionsAgainstPerpetratorsThunk,
} from '../../logic/ActionsAgainstPerpetratorsThunk';
import { RECOMMENDED_ACTION_DROPDOWN_OPTIONS } from '../../constants/dropdown';
import ClosureNoteModal from './ClosureNoteModal';

interface ActionsAgainstInvolvedPartiesTableV2Props {
  caseId: string;
}

const toUIRow = (
  action: ActionAgainstPerpetrator
): ActionAgainstPerpetratorUI => ({
  ...action,
  id: action.stakeholder_id,
  is_added: false,
  is_dirty: false,
});

const buildEmptyRow = (): ActionAgainstPerpetratorUI => ({
  id: crypto.randomUUID(),
  stakeholder_id: '',
  recommended_actions: [],
  recommended_action_approver: null,
  recommended_action_implementation_date: null,
  implementation_owner: null,
  closure_note: null,
  is_added: true,
  is_dirty: false,
});

const isRowComplete = (row: ActionAgainstPerpetratorUI) =>
  row.stakeholder_id.trim().length > 0 &&
  (row.recommended_actions?.length ?? 0) > 0;

const toPayload = (
  row: ActionAgainstPerpetratorUI
): ActionAgainstPerpetrator => ({
  stakeholder_id: row.stakeholder_id,
  recommended_actions: row.recommended_actions ?? [],
  recommended_action_approver: row.recommended_action_approver || null,
  recommended_action_implementation_date:
    row.recommended_action_implementation_date || null,
  implementation_owner: row.implementation_owner || null,
  closure_note: row.closure_note || null,
});

const toClearedPayload = (stakeholderId: string): ActionAgainstPerpetrator => ({
  stakeholder_id: stakeholderId,
  recommended_actions: [],
  recommended_action_approver: null,
  recommended_action_implementation_date: null,
  implementation_owner: null,
  closure_note: null,
});

const ActionsAgainstInvolvedPartiesTableV2: React.FC<
  ActionsAgainstInvolvedPartiesTableV2Props
> = ({ caseId }) => {
  const dispatch = useAppDispatch();

  const savedActions = useAppSelector(selectRecordedPerpetratorActions);
  const fetchStatus = useAppSelector(selectPerpetratorActionsStatus);
  const fetchError = useAppSelector(selectPerpetratorActionsError);
  const partyOptions = useAppSelector(selectInvolvedPartyOptions);
  const collaborators = useAppSelector(getCollaborators);

  const [localRows, setLocalRows] = useState<ActionAgainstPerpetratorUI[] | null>(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [modalVariant, setModalVariant] = useState<'edit' | 'view'>('edit');

  useEffect(() => {
    if (caseId) {
      dispatch(fetchActionsAgainstPerpetratorsThunk({ caseId }));
    }
  }, [caseId, dispatch]);

  const savedRows = useMemo(() => savedActions.map(toUIRow), [savedActions]);

  const implementationOwnerOptions = useMemo(
    () =>
      collaborators.map((collaborator) => ({
        name: `${collaborator.user_name} - ${collaborator.department}`,
        value: collaborator.user_id,
      })),
    [collaborators]
  );

  const dataRowsSource = localRows ?? savedRows;

  const partyOptionsInUse = partyOptions.map((option) => ({
    ...option,
    disabled:
      option.disabled ||
      dataRowsSource.some((row) => row.stakeholder_id === option.value),
  }));

  const hasIncompleteNewRow = dataRowsSource.some(
    (row) => row.is_added && !isRowComplete(row)
  );

  const rowsToSave = dataRowsSource.filter(
    (row) => (row.is_added || row.is_dirty) && isRowComplete(row)
  );

  const clearedRowIds = dataRowsSource
    .filter((row) => !row.is_added && row.is_dirty && !isRowComplete(row))
    .map((row) => row.stakeholder_id);

  const deletedRowIds = [...pendingDeleteIds, ...clearedRowIds];

  const isDirty = rowsToSave.length > 0 || deletedRowIds.length > 0;

  if (fetchStatus === 'idle' || fetchStatus === 'loading') {
    return (
      <div className="mt-2">
        <CustomLoadingSpinner
          title="Loading Recommended Action Against Parties..."
          subtitle={RETRIEVING_DATA_SUBTITLE}
          minHeight="min-h-[100px]"
        />
      </div>
    );
  }

  if (fetchStatus === 'failed') {
    return (
      <ErrorDisplay
        title="Failed to load parties involved"
        error={fetchError}
        onRetry={() => dispatch(fetchActionsAgainstPerpetratorsThunk({ caseId }))}
      />
    );
  }

  const handleAddRow = () => {
    if (hasIncompleteNewRow) return;

    setLocalRows((prev) => [...(prev ?? savedRows), buildEmptyRow()]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const row = dataRowsSource[rowIndex];
    if (!row) return;

    if (!row.is_added) {
      setPendingDeleteIds((prev) => [...prev, row.stakeholder_id]);
    }

    setLocalRows((prev) => (prev ?? savedRows).filter((_, i) => i !== rowIndex));
  };

  const handleTableChange = <T extends keyof ActionAgainstPerpetratorUI>(
    rowIndex: number,
    value: ActionAgainstPerpetratorUI[T],
    field: T
  ) => {
    if (isSaving) return;

    setLocalRows((prev) => {
      const current = prev ?? savedRows;

      return current.map((row, index) =>
        index === rowIndex
          ? { ...row, [field]: value, is_dirty: !row.is_added }
          : row
      );
    });
  };

  const selectedRow = dataRowsSource.find((row) => row.id === selectedRowId);

  const handleClosureNoteClick = (rowIndex: number) => {
    const row = dataRowsSource[rowIndex];
    if (!row) return;

    const hasNote = (row.closure_note ?? '').trim().length > 0;

    setSelectedRowId(row.id);
    setModalVariant(hasNote ? 'view' : 'edit');
  };

  const handleSaveClosureNote = (note: string) => {
    if (!selectedRowId) return;

    setLocalRows((prev) =>
      (prev ?? savedRows).map((row) =>
        row.id === selectedRowId
          ? { ...row, closure_note: note, is_dirty: !row.is_added }
          : row
      )
    );
  };

  const handleCloseClosureNoteModal = () => {
    setSelectedRowId(null);
  };

  const handleDiscardChanges = () => {
    setLocalRows(null);
    setPendingDeleteIds([]);
    setSelectedRowId(null);
  };

  const handleSave = async () => {
    if (!isDirty) return;

    try {
      setIsSaving(true);

      await dispatch(
        upsertActionsAgainstPerpetratorsThunk({
          caseId,
          request: {
            actions_against_perpetrators: [
              ...rowsToSave.map(toPayload),
              ...deletedRowIds.map(toClearedPayload),
            ],
          },
        })
      ).unwrap();

      setLocalRows(null);
      setPendingDeleteIds([]);
      showSuccessToast('Actions saved successfully!');
    } catch (error) {
      showErrorToast('Failed to save actions');
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const columnConfigs: IColumnConfig<ActionAgainstPerpetratorUI>[] = [
    {
      headerName: 'Party Involved',
      field: 'stakeholder_id',
      type: 'select',
      getValue: (data) => data.stakeholder_id,
      isEditable: (_rowIndex, rowData) => rowData.is_added,
      onChange: handleTableChange,
      options: partyOptionsInUse,
      selectCellProps: {
        placeholder: 'Add',
        showTitleSubTitle: true,
        showTooltip: true,
        titleClassName: 'max-w-[190px]',
        allowOpenWhenReadOnly: true,
        hideDisabledOptions: true,
      },
    },
    {
      headerName: 'Recommended Actions',
      field: 'recommended_actions',
      type: 'multiselect',
      getValue: (data) => data.recommended_actions ?? [],
      isEditable: true,
      onChange: handleTableChange,
      options: RECOMMENDED_ACTION_DROPDOWN_OPTIONS,
      multiselectCellProps: {
        name: 'Recommended Actions',
        rootContainerClassName: 'min-w-[160px]',
        dropdownClassName: 'min-w-[250px] min-h-[380px]',
      },
    },
    {
      headerName: 'Implementation Owner',
      field: 'implementation_owner',
      type: 'select',
      getValue: (data) => data.implementation_owner,
      isEditable: true,
      onChange: handleTableChange,
      options: implementationOwnerOptions,
      selectCellProps: {
        placeholder: 'Add',
        showTitleSubTitle: true,
        showTooltip: true,
        dropdownClassName: 'w-max min-w-full',
        titleClassName: 'font-sans font-normal text-sm leading-5 text-[#0A0A0A]',
      },
    },
    {
      headerName: 'Approved By',
      field: 'recommended_action_approver',
      type: 'text',
      getValue: (data) => data.recommended_action_approver,
      isEditable: true,
      onChange: handleTableChange,
      textCellProps: {
        textareaClassName: 'max-w-[197px]',
        numberOfRows: 1,
      },
    },
    {
      headerName: 'Implementation Date',
      field: 'recommended_action_implementation_date',
      type: 'date',
      getValue: (data) => data.recommended_action_implementation_date,
      isEditable: true,
      onChange: handleTableChange,
    },
    {
      headerName: 'Closure Note',
      field: 'closure_note',
      type: 'textbutton',
      getValue: (data) =>
        (data.closure_note ?? '').trim().length > 0
          ? 'View notes'
          : '+ Add closure note',
      onClick: handleClosureNoteClick,
    },
  ];

  const dataRows: IDataRow<ActionAgainstPerpetratorUI>[] = dataRowsSource.map(
    (row) => ({
      data: row,
      isEditable: true,
      widgets: isSaving ? [] : ['delete', 'add'],
      onDelete: handleDeleteRow,
      onAdd: handleAddRow,
    })
  );

  const renderDefaultTableBody = () => (
    <div className="flex flex-col items-center justify-center py-8 px-4">
      <NoDataIcon />
      <span className="text-[#0A0A0A] font-sans font-semibold text-base leading-[1.5rem] text-center mt-4 mb-2 max-w-full">
        No recommendations yet
      </span>
      <span className="text-[#737373] font-sans font-normal text-sm leading-[1.25rem] text-center mb-4 max-w-md">
        Add recommendations to track actions and share them with the right
        owners
      </span>
      <ButtonV2 onClick={handleAddRow}>
        <Plus className="w-4 h-4 mr-1" /> Add
      </ButtonV2>
    </div>
  );

  const renderDiscardChangesButton = () => (
    <ButtonV2
      variant="outline"
      onClick={handleDiscardChanges}
      disabled={isSaving}
      className={cn(
        'font-sans font-medium text-sm leading-5',
        isSaving && 'opacity-50 cursor-not-allowed'
      )}
      style={{
        width: '137.5px',
        height: '36px',
        borderColor: '#3551F3',
        color: '#3551F3',
        gap: '8px',
      }}
    >
      Discard Changes
    </ButtonV2>
  );

  const renderSaveButton = () => {
    const isDisabled = isSaving || !isDirty || hasIncompleteNewRow;

    return (
      <ButtonV2
        variant="primary"
        onClick={handleSave}
        disabled={isDisabled}
        className={cn(
          'font-sans font-medium text-sm leading-5 flex items-center justify-center w-[137.5px] h-[36px] gap-2',
          isDisabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <Save className="w-4 h-4 relative right-[4px]" />
        <span className="font-sans font-medium text-sm leading-5 align-middle">
          {isSaving ? 'Saving...' : 'Save'}
        </span>
      </ButtonV2>
    );
  };

  const renderFooter = () => (
    <div className="w-full min-h-[44px] flex justify-end items-center gap-5 rounded-lg py-2 px-2.5 border bg-[#F1F5F9] mt-4">
      <div className="flex flex-col items-end flex-1">
        <span className="font-sans font-semibold text-sm leading-5 tracking-normal text-[#737373]">
          {hasIncompleteNewRow
            ? 'Select a party and at least one recommended action to enable Save.'
            : 'You have made changes, save now'}
        </span>
      </div>
      {renderDiscardChangesButton()}
      {renderSaveButton()}
    </div>
  );

  return (
    <div className="w-full">
      <BaseDataTable
        columnConfigs={columnConfigs}
        dataRows={dataRows}
        showDefaultTableBody={dataRowsSource.length === 0}
        defaultTableBody={renderDefaultTableBody()}
      />
      {(isDirty || hasIncompleteNewRow) && renderFooter()}
      {selectedRow && (
        <ClosureNoteModal
          isOpen
          onClose={handleCloseClosureNoteModal}
          variant={modalVariant}
          initialNote={selectedRow?.closure_note ?? ''}
          onSave={handleSaveClosureNote}
        />
      )}
    </div>
  );
};

export default ActionsAgainstInvolvedPartiesTableV2;
